import { Providers } from "./manager.js";
import { ConfigProvider } from "./config/provider.js";
import { newSecretStorage } from "./config/secrets.js";
import { storedConfigSchema } from "./config/schemas.js";
import { AuthProvider } from "./auth/provider.js";
import { DummyAnalyticsProvider } from "./analytics/provider.js";
import { getAppConfig } from "./app.js";
import { logger } from "./logger.js";
import { Config, CredentialsConfig } from "./config/schemas.js";

/**
 * Session authentication information for creating user-specific providers
 */
export interface UserSessionAuth {
  accessToken?: string;
  refreshToken?: string;
  apiKey?: string;
  username: string;
  isApiKeyAuth: boolean;
}

/**
 * Creates a complete set of isolated providers for a specific user session.
 * This ensures each user has their own configuration without file persistence
 * or cross-contamination between users.
 *
 * @deprecated Use MultiTenantProviderManager instead for better lifecycle management
 * @param sessionAuth - The user's authentication information
 * @param baseConfig - Optional base configuration to use (defaults to app config)
 * @returns Complete set of providers for the user
 */
export async function createUserProviders(
  sessionAuth: UserSessionAuth,
  baseConfig?: Config,
): Promise<Providers> {
  logger.info("Creating user-specific providers", {
    username: sessionAuth.username,
    isApiKeyAuth: sessionAuth.isApiKeyAuth,
    hasBaseConfig: !!baseConfig,
  });

  // Get app configuration
  const appConfig = await getAppConfig();

  // Determine initial configuration
  const initialConfig = baseConfig || {
    app: appConfig,
    api: appConfig.demoSandbox.api,
  };

  // Create credentials based on auth type
  const credentials: CredentialsConfig =
    sessionAuth.isApiKeyAuth && sessionAuth.apiKey
      ? {
          accessToken: "", // Empty for API key auth
          apiKey: sessionAuth.apiKey,
        }
      : {
          accessToken: sessionAuth.accessToken || "",
        };

  // Create in-memory secret storage
  const secretStorage = await newSecretStorage(storedConfigSchema, "in-memory");

  // Create config provider with in-memory storage
  const configProvider = new ConfigProvider(appConfig, secretStorage);

  // Initialize the config provider with user-specific values
  await configProvider.update({
    api: initialConfig.api,
    credentials,
    identity: {
      user: {
        id: sessionAuth.username,
        email: `${sessionAuth.username}@toolprint.local`, // Placeholder email
      },
    },
  });

  // Create auth provider with the user-specific config
  const authProvider = new AuthProvider({ configProvider });

  // Create analytics provider (using dummy for user sessions)
  const analyticsProvider = new DummyAnalyticsProvider({
    appConfig,
    configProvider,
  });

  // Create a minimal flags provider that doesn't require authentication
  // for user-specific sessions (flags are typically app-wide)
  const flagsProvider = {
    validateFlagName: (flagName: string) => {
      // No-op for user sessions
    },
    all: async () => ({}),
    value: async (flagName: string) => undefined,
  } as any; // Type assertion needed due to private class

  logger.debug("User providers created successfully", {
    username: sessionAuth.username,
  });

  return {
    configProvider,
    authProvider,
    analyticsProvider,
    flagsProvider,
  };
}

/**
 * Creates user providers from an existing base provider set.
 * This is useful when you want to derive user-specific providers from
 * a global provider configuration.
 *
 * @deprecated Use MultiTenantProviderManager instead for better lifecycle management
 * @param baseProviders - The base providers to derive from
 * @param sessionAuth - The user's authentication information
 * @returns Complete set of providers for the user
 */
export async function createUserProvidersFromBase(
  baseProviders: Providers,
  sessionAuth: UserSessionAuth,
): Promise<Providers> {
  // Get the base configuration
  const baseConfig = await baseProviders.configProvider.getConfig();

  // Create new providers with the base configuration
  return createUserProviders(sessionAuth, baseConfig);
}
